/**
 * "Add relation" in the input settings: shows the source table where it
 * lives (panned to on the canvas, also when it stands inside a note there;
 * scrolled to on a page), adds a relation column to it and opens that
 * column's menu with its relation settings — the same menus the table
 * header opens.
 */
import { openColumnMenu } from '@affine/core/blocksuite/streamlain/database/column-menu-hooks';
import { Bound } from '@blocksuite/affine/global/gfx';
import type { BlockModel, Store } from '@blocksuite/affine/store';
import { createDatabaseBlockDataSource } from '@blocksuite/affine-block-database';
import {
  getScrollContainer,
  streamLainAnimationController,
} from '@blocksuite/affine-shared/utils';
import { availableRelationColumnName } from '@streamlain/database-identity';

import { cinematicNavigateTo } from '../utils/cinematic-viewport';
import {
  canvasAreaOfRendered,
  canvasBlockOf,
  canvasCenterFor,
} from './canvas-view-center';

interface EdgelessViewport {
  centerX: number;
  centerY: number;
  zoom: number;
  /** Client rect of the canvas. */
  left: number;
  width: number;
  height: number;
  setCenter: (x: number, y: number) => void;
  setZoom: (zoom: number, focusPoint?: { x: number; y: number }) => void;
  toModelCoordFromClientCoord: (point: [number, number]) => number[];
}

const blockElement = (blockId: string) =>
  document.querySelector<HTMLElement>(
    `[data-block-id="${CSS.escape(blockId)}"]`
  );

const boundOf = (model: BlockModel): Bound | null => {
  const xywh = (model as { xywh?: unknown }).xywh;
  return typeof xywh === 'string' ? Bound.deserialize(xywh) : null;
};

/** Client x from which the open automation settings cover the canvas. */
function settingsPaneLeft(): number | null {
  const rect = document
    .querySelector('[data-testid="automation-settings-panel"]')
    ?.getBoundingClientRect();
  return rect && rect.width > 0 ? rect.left : null;
}

async function showBlock(model: BlockModel): Promise<void> {
  const viewport = (
    document.querySelector('affine-edgeless-root') as
      | (Element & { gfx?: { viewport?: EdgelessViewport } })
      | null
  )?.gfx?.viewport;
  // On the canvas lies the block itself, or the note that holds it.
  const canvasBlock = canvasBlockOf(model);
  const onCanvas = viewport ? boundOf(canvasBlock) : null;
  if (viewport && onCanvas) {
    const zoom = Math.max(viewport.zoom, 1);
    const panTo = async (bound: Bound) => {
      const [x, y] = canvasCenterFor(viewport, bound, zoom, settingsPaneLeft());
      await cinematicNavigateTo(viewport, x, y, { targetZoom: zoom });
    };
    if (canvasBlock === model) {
      await panTo(onCanvas);
      return;
    }
    // Inside a note the table stands somewhere in it, often far below the
    // note's top: go to where the note renders the table.
    const inNote = () => {
      const rect = blockElement(model.id)?.getBoundingClientRect();
      if (!rect || rect.width <= 0 || rect.height <= 0) return null;
      const area = canvasAreaOfRendered(
        rect,
        point => viewport.toModelCoordFromClientCoord(point),
        onCanvas
      );
      return area ? new Bound(area.x, area.y, area.w, area.h) : null;
    };
    let bound = inNote();
    if (!bound) {
      // Not rendered yet (the note is out of view): the note first.
      await panTo(onCanvas);
      bound = inNote();
    }
    if (bound) await panTo(bound);
    return;
  }
  const element = blockElement(model.id);
  if (!element) return;
  const container = getScrollContainer(element);
  const offset =
    element.getBoundingClientRect().top - container.getBoundingClientRect().top;
  await streamLainAnimationController.scrollTo(container, {
    top: Math.max(0, container.scrollTop + offset - 48),
  }).finished;
}

const nextFrame = () =>
  new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

/** Whether the block shows a table header, where column menus open. */
const showsTableHeader = (blockId: string) =>
  !!blockElement(blockId)?.querySelector(
    'virtual-table-header, affine-database-column-header'
  );

/**
 * Adds a column to the database block's current view (or the block when it
 * has no view) and returns its id.
 */
export function addSourceTableColumn(
  doc: Store,
  model: BlockModel,
  column: { type: string; name: string },
  data?: Record<string, unknown>
): string {
  let columnId: string | undefined;
  doc.transact(() => {
    const dataSource = createDatabaseBlockDataSource(model as never);
    const propertyOwner =
      dataSource.viewManager.currentView$.value ?? dataSource;
    columnId = propertyOwner.propertyAdd('end', column);
    if (columnId && data) dataSource.propertyDataSet(columnId, data);
  });
  if (!columnId) throw new Error(`${column.type} column could not be created.`);
  return columnId;
}

/**
 * Shows the source table, adds a relation column and opens its settings.
 * Resolves false, without adding a column, when the table is not shown as
 * a table (no header to open the column menu at).
 */
export async function addRelationColumn(
  doc: Store,
  model: BlockModel,
  baseName: string
): Promise<boolean> {
  await showBlock(model);
  if (!showsTableHeader(model.id)) return false;
  const dataSource = createDatabaseBlockDataSource(model as never);
  const names = dataSource
    .getAllPropertyIds()
    .map(id => dataSource.propertyNameGet(id));
  const columnId = addSourceTableColumn(doc, model, {
    type: 'relation',
    name: availableRelationColumnName(baseName, names),
  });
  await nextFrame();
  await nextFrame();
  return openColumnMenu(model.id, columnId);
}
